import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { uiConfig as uiConfigCodeSystems } from '../../../conf/codeSystems';
import { uiConfig as uiConfigValueSets } from '../../../conf/valueSets';
import { uiConfig as uiConfigLists, crudConfig as crudConfigLists } from '../../../conf/lists';
import { listTypes } from '../../../conf/enums';

// Components
import Form from '../../../../components/Form';
import FeedbackCard from '../../../../components/FeedbackCard';
import { Dialog } from '../../../../components/Dialog';
import { DialogHeader } from '../../../../components/DialogHeader';
import { DialogBody } from '../../../../components/DialogBody';
import { DialogFooter } from '../../../../components/DialogFooter';
import { Button } from '../../../../components/Button';
import { Spinner } from '../../../../components/Spinner';
import { MaterialIcon } from '../../../../components/MaterialIcon';
import TabbedContainer from '../../../../components/TabbedContainer';
import { ConfirmationModal } from '../../../../components/ConfirmationModal';
import { ListItemsTable } from '../listItemsTable';

// Hooks
import { useInsertList } from '../../api/hooks/useInsertList';
import { useUpdateList } from '../../api/hooks/useUpdateList';
import { useGetListById } from '../../api/hooks/useGetListById';
import { useGetCodeSystems } from '../../api/hooks/useGetCodeSystems';
import { useGetValueSets } from '../../api/hooks/useGetValueSets';

export const SpinnerCont = styled.div`
    display: flex;
    justify-content: center;
`;

const AddEdit = ({ formModel, selectedListId, closeDialog, sessionToken, saveDisabled, onMount, isExistingDoc }) => {
    const [listId, setListId] = React.useState(selectedListId);
    const isNew = React.useMemo(() => listId === 'new', [listId]);
    const [wasSuccessful, setWasSuccessful] = React.useState(false);
    const [buttonClicked, setButtonClicked] = React.useState();
    const CloseDialog = () => {
        return typeof closeDialog === 'function' ? closeDialog() : null;
    };
    const [showDiscardChangesModal, setShowDiscardChangesModal] = React.useState(false);
    const mutationConfig = {
        crudConfig: crudConfigLists,
        sessionToken,
        onErrorCb: (e) => {
            setFatalError(e);
            setWasSuccessful(false);
        },
        onSettledCb: () => {
            setButtonClicked();
        }
    };
    const insertList = useInsertList({
        ...mutationConfig,
        onSuccessCb: ({ data, context = {} }) => {
            setFatalError(null);
            setWasSuccessful(true);
            setEdited(false);
            setListId(data);
            if (context?.closeOnSuccess) {
                setTimeout(() => {
                    CloseDialog();
                }, 500);
            }
        }
    });
    const updateList = useUpdateList({
        ...mutationConfig,
        onSuccessCb: ({ context = {} }) => {
            setFatalError(null);
            setWasSuccessful(true);
            setEdited(false);
            if (context?.closeOnSuccess) {
                setTimeout(() => {
                    CloseDialog();
                }, 500);
            }
        }
    });

    const dataSetQueryConfig = {
        sessionToken,
        queryOptions: {
            enabled: false
        }
    };
    const listQuery = useGetListById({
        sessionToken,
        listId,
        queryOptions: {
            enabled: false
        }
    });

    React.useEffect(() => {
        if (listId !== 'new' && !isNew) {
            listQuery.refetch();
        }
    }, [listId, isNew]);
    const codeSystemsQuery = useGetCodeSystems(dataSetQueryConfig);
    const valueSetsQuery = useGetValueSets(dataSetQueryConfig);
    const [compose, updateCompose] = React.useState(false);
    const [edited, setEdited] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [fatalError, setFatalError] = React.useState(null);
    const [codeSystemId, setCodeSystemId] = React.useState('');
    const [valueSetId, setValueSetId] = React.useState('');
    const [listState, setListState] = React.useState(null);
    React.useEffect(() => {
        if (!isNew) {
            if (listQuery?.isLoading) {
                setListState({});
                return;
            }
            if (typeof listQuery?.data === 'object') {
                setListState(listQuery?.data);
            } else {
                // This is an error
                alert('Subject data not found, please refresh the page');
                setListState({});
            }
            return;
        } else {
            setListState(typeof uiConfigLists.newDoc === 'object' ? uiConfigLists.newDoc : {});
        }
    }, [isNew, listId, listQuery?.isLoading, listQuery?.data]);
    const updateListState = React.useCallback(
        (update) => {
            setListState({
                ...listState,
                ...update,
                dtoState: 'UPDATED'
            });
            setEdited(true);
        },
        [listState, setListState]
    );
    React.useEffect(() => {
        if (edited && wasSuccessful) {
            setWasSuccessful(false);
        }
    }, [edited]);

    const prepareDocument = (docState, newList) => {
        const preparedDocument = ((doc) => {
            switch (doc.listType) {
                case listTypes.VALUE_SET:
                    return {
                        ...doc,
                        cdsCodeSystemDTO: null,
                        enumClass: null,
                        valueSetDTO: valueSetsQuery?.data?.find((valueSet) => {
                            return valueSet[uiConfigValueSets.primaryKey] === valueSetId;
                        })
                    };
                    break;
                case listTypes.JAVA_ENUM:
                    return {
                        ...doc,
                        cdsCodeSystemDTO: null,
                        valueSetDTO: null
                    };
                    break;
                case listTypes.CODE_SYSTEM:
                    return {
                        ...doc,
                        valueSetDTO: null,
                        enumClass: null,
                        cdsCodeSystemDTO: codeSystemsQuery?.data?.find((codeSystem) => {
                            return codeSystem[uiConfigCodeSystems.primaryKey] === codeSystemId;
                        })
                    };
                    break;
                default:
                    return docState;
                    break;
            }
        })(docState);

        return {
            ...preparedDocument,
            dtoState: newList ? 'NEW' : 'UPDATED'
        };
    };

    const exposedValues = React.useMemo(
        () => ({
            codeSystem: codeSystemsQuery?.data,
            valueSet: valueSetsQuery?.data
        }),
        [codeSystemsQuery, valueSetsQuery]
    );

    const onApplyFormState = (formState) => {
        if (saveDisabled) {
            console.warn('Authoring Interface is currently read-only.');
            return;
        }
        let update = prepareDocument(
            {
                ...listState,
                ...formState
            },
            isNew
        );
        updateCompose(false);
        let { valid } = validate(update);

        if (!valid) return;
        if (isExistingDoc(update)) {
            setFatalError('Matching document already exists.');
            return;
        }
        if (isNew) {
            insertList.mutate({ newList: update, context: { closeOnSuccess: buttonClicked === 'save' } });
        } else {
            updateList.mutate({ update, context: { closeOnSuccess: buttonClicked === 'save' } });
        }
    };

    const isLoading = React.useMemo(() => insertList.isLoading || updateList.isLoading, [insertList, updateList]);

    const fetchResourceLoad = (v) => {
        ({
            codeSystem: codeSystemsQuery?.refetch,
            valueSet: valueSetsQuery?.refetch
        }?.[v]?.());
    };

    const maybeUpdateOptionsMap = (v) => {
        let supportData = exposedValues?.[v];

        if (!supportData || supportData.length === 0) {
            fetchResourceLoad(v);
        }
    };

    // Temporary validation solution
    const validate = (formState) => {
        let errors = {};
        for (let field of formModel) {
            let { valid, failed } = field.validation(formState[field.accessor]);
            if (!valid) {
                errors[field.accessor] = 'Invalid Input: ' + failed.join(', ');
            }
            // Add additional validation here;
        }
        // Add relational validation here

        setErrors(errors);
        let valid = Object.keys(errors).length === 0;
        return { valid, failureMsg: '' };
    };

    const prepareOptions = () => {
        let optionsOverride = {};
        optionsOverride.cdsCodeSystemDTO = exposedValues.codeSystem?.reduce((agg, v, i) => {
            agg[v[uiConfigCodeSystems.primaryKey]] = `${v.name} [${v.oid}]`;
            return agg;
        }, {});
        optionsOverride.valueSetDTO = exposedValues.valueSet?.reduce((agg, v, i) => {
            agg[v[uiConfigValueSets.primaryKey]] = `${v.oid} - ${v.name} - ${v.version}`;
            return agg;
        }, {});
        return optionsOverride;
    };

    let optionsOverride = prepareOptions();

    for (let key in optionsOverride) {
        let subIndex = formModel.findIndex((v) => v.accessor === key);
        if (subIndex !== -1) {
            formModel[subIndex].options = optionsOverride[key];
        }
    }

    return (
        <>
            <div style={{ height: `30rem` }}>
                <Dialog close={CloseDialog} size="lg" visible id="list-manager-addEdit">
                    <DialogHeader close={CloseDialog}>
                        {isNew ? 'Add List' : `Edit ${listState?.name} - ${listState?.code}`}
                    </DialogHeader>
                    <DialogBody>
                        {fatalError != null && (
                            <FeedbackCard
                                intent={'danger'}
                                feedback={{
                                    heading: `An error has occurred.`,
                                    message:
                                        typeof fatalError === 'string'
                                            ? fatalError
                                            : `Please wait a few moments before trying your request
again.`
                                }}
                                details={fatalError}
                            />
                        )}
                        <TabbedContainer
                            expandHeaderX
                            tabs={{
                                details: {
                                    label: 'List Details',
                                    alwaysMounted: true,
                                    children: (
                                        <>
                                            {isNew ? 'Enter' : 'Adjust'} required information below.
                                            <p style={{ margin: `1.33rem 0 0 0` }}></p>
                                            {!isNew &&
                                            (listQuery?.isLoading ||
                                                listState === null ||
                                                Object.keys(listState).length === 0) ? (
                                                <SpinnerCont>
                                                    <Spinner size={'2.3rem'} color={'#1E527E'} />
                                                </SpinnerCont>
                                            ) : (
                                                <Form
                                                    formModel={formModel}
                                                    isNew={isNew}
                                                    data={listState}
                                                    errors={errors}
                                                    compose={compose}
                                                    onApplyFormState={onApplyFormState}
                                                    setEdited={setEdited}
                                                    fatalError={fatalError}
                                                    onMount={onMount}
                                                    hookFxns={{
                                                        maybeUpdateOptionsMap,
                                                        updateCodeSystem: setCodeSystemId,
                                                        updateValueSet: setValueSetId
                                                    }}
                                                />
                                            )}
                                        </>
                                    )
                                },
                                items: {
                                    label: 'List Items',
                                    disabled: isNew,
                                    children: (
                                        <>
                                            {listQuery?.isLoading ? (
                                                <SpinnerCont>
                                                    <Spinner size={'2.3rem'} color={'#1E527E'} />
                                                </SpinnerCont>
                                            ) : (
                                                <ListItemsTable
                                                    data={listState}
                                                    sessionToken={sessionToken}
                                                    onSave={(update) => updateListState(update)}
                                                />
                                            )}
                                        </>
                                    )
                                }
                            }}
                        />
                    </DialogBody>
                    <DialogFooter>
                        {isLoading && <div style={{ marginRight: '0.5rem' }}>Loading...</div>}
                        {!isLoading && wasSuccessful && (
                            <div style={{ marginRight: '0.5rem' }}>
                                <MaterialIcon icon={'done_outline'} color={'#04846D'} />
                            </div>
                        )}
                        <Button
                            text
                            appearance="primary"
                            disabled={!edited || saveDisabled}
                            onClick={() => {
                                setButtonClicked('save');
                                updateCompose(true);
                            }}
                            loading={isLoading}
                        >
                            Save List
                        </Button>
                        <Button
                            text
                            appearance="secondary"
                            disabled={!edited || saveDisabled}
                            onClick={() => {
                                setButtonClicked('apply');
                                updateCompose(true);
                            }}
                            loading={isLoading}
                        >
                            Apply
                        </Button>
                        <Button
                            text
                            appearance="secondary"
                            onClick={() => {
                                if (edited) {
                                    setShowDiscardChangesModal(true);
                                } else {
                                    CloseDialog();
                                }
                            }}
                        >
                            Cancel
                        </Button>
                    </DialogFooter>
                </Dialog>
            </div>
            {showDiscardChangesModal ? (
                <ConfirmationModal
                    header={`Discard unsaved changes?`}
                    body={
                        'You have unsaved changes which will be discarded upon closing this modal. Do you wish to proceed?'
                    }
                    confirmButtonText={'Yes, Discard Changes'}
                    confirmButtonProps={{ appearance: 'destructive', text: true }}
                    closeDialog={() => setShowDiscardChangesModal(false)}
                    onConfirm={() =>
                        new Promise((resolve) => {
                            CloseDialog();
                            resolve();
                        })
                    }
                />
            ) : null}
        </>
    );
};

export default AddEdit;

AddEdit.propTypes = {
    listId: PropTypes.string,
    formModel: PropTypes.arrayOf(PropTypes.object),
    isNew: PropTypes.bool,
    closeDialog: PropTypes.func,
    isExistingDoc: PropTypes.func,
    onMount: PropTypes.func,
    sessionToken: PropTypes.string,
    saveDisabled: PropTypes.bool
};
